/**
 * F7 — Phase-stage-weight editor (v2).
 * - wbsPercent JSON removed from config GET/PUT (§C: blob gone)
 * - editor loads getPhaseStageWeights({projectPhaseId}), one row per active construction stage
 * - keeps 100%-sum validator (WBS_SUM_TOLERANCE)
 * - saves via putPhaseStageWeights({projectPhaseId, stages:[{wbsStageId,weight,isActive}]})
 */
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@material-tailwind/react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useFieldArray, useForm } from "react-hook-form";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";
import CustomInput from "../../components/CustomInput";
import { EditButton, OutlineButton, PrimaryButton } from "../../components/Buttons";
import CustomCollapseCard from "../../components/CustomCollapseCard";
import usePrivilege from "../../hooks/usePrivilege";
import AgreementUpload from "../../components/ProjectScreens/AgreementUpload";
import {
  encryptForTransit,
  decryptFromTransit,
  normalizeTransitCiphertextBase64,
} from "../../utils/common";
import { FormModal, yupResolver } from "../../components/form";
import {
  projectBankDetailsSchema,
  projectGovtChargesSchema,
} from "../../services/projects/schemas";
// v2: phase-stage-weights service
import { getPhaseStageWeights, putPhaseStageWeights } from "../../services/wbs/index";

/** Allowed drift when comparing WBS weightage sum to 100%. */
const WBS_SUM_TOLERANCE = 0.01;

/** Preset values for bank account type (saved as `bankAccount` in encrypted bank details). */
const BANK_ACCOUNT_TYPE_OPTIONS = [
  "Savings",
  "Current",
  "Cash Credit (CC)",
  "Overdraft (OD)",
  "NRE",
  "NRO",
  "FCNR",
];

const ProjectSettings = ({ projectPhases: projectPhasesProp } = {}) => {
  const [project, setProject] = useState(null);
  const { hasPrivilege } = usePrivilege();
  const [newStampDutyPercent, setNewStampDutyPercent] = useState("");
  const [newGstPercent, setNewGstPercent] = useState("");
  const [newRegistrationPercent, setNewRegistrationPercent] = useState("");
  // v2: localWbsPercent/isEditingProjectCompletion/wbsRangeInvalid removed (§C: wbsPercent blob gone).
  const [govtMaxExceeded, setGovtMaxExceeded] = useState({
    stamp: false,
    gst: false,
    registration: false,
  });

  const [editableSettings, setEditableSettings] = useState({
    bankName: "",
    bankAccount: "",
    accountNo: "",
    branch: "",
    ifscCode: "",
    stamp_duty_percent: [],
    gst_percent: [],
    registration_percent: [],
    costSheetTerms: "",
    bookingLetterTerms: "",
    cancellationLetterTerms: "",
    handoverLetterTerms: ""
  });
  const [initialBankDetails, setInitialBankDetails] = useState({
    bankName: "",
    bankAccount: "",
    accountNo: "",
    branch: "",
    ifscCode: "",
  });

  const [displayLetters, setDisplayLetters] = useState({
    costSheet: "",
    bookingLetter: "",
    cancellationLetter: "",
    handoverLetter: ""
  });

  // `isEditing*` flags are kept as transient signals to handleSave (which
  // decides whether to send bank_details or configuration on the PUT). The
  // user-facing Edit affordance is now modal-based; the flag is set true
  // momentarily inside the modal's onSubmit and reset by handleSave.
  const [isEditingBankDetails, setIsEditingBankDetails] = useState(false);
  const [isEditingOtherSettings, setIsEditingOtherSettings] = useState(false);
  const [bankDetailsModalOpen, setBankDetailsModalOpen] = useState(false);
  const [govtChargesModalOpen, setGovtChargesModalOpen] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openStates, setOpenStates] = useState({
    costSheet: false,
    bookingLetter: false,
    cancellationLetter: false,
    handoverLetter: false
  });

  const { userInfo } = useUser();
  const { projectId } = useParams();

  // v2: phase-stage-weights state (replaces wbsPercent JSON blob)
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [phaseStageWeights, setPhaseStageWeights] = useState([]); // ProjectPhaseStageData[]
  const [localPhaseStageWeights, setLocalPhaseStageWeights] = useState([]); // editable copy
  const [isEditingPhaseWeights, setIsEditingPhaseWeights] = useState(false);
  const [savingPhaseWeights, setSavingPhaseWeights] = useState(false);
  const [phaseWeightsError, setPhaseWeightsError] = useState(null);
  const projectPhases = projectPhasesProp || [];

  const inputStyle = {
    border: "1px solid #e2e8f0",
    borderRadius: "0.375rem",
    padding: "0.45rem 0.9rem",
    fontSize: "1rem",
    width: "90%",
    outline: "none",
    transition: "border-color 0.15s ease-in-out",
  };

  /** Use with CustomInput `error` prop so the component’s red border is visible (no inline border override). */
  const inputStyleNoBorder = {
    borderRadius: "0.375rem",
    padding: "0.45rem 0.9rem",
    fontSize: "1rem",
    width: "90%",
    outline: "none",
    transition: "border-color 0.15s ease-in-out",
  };

  const bankAccountTypeOptionsForSelect = useMemo(() => {
    const v = String(editableSettings.bankAccount || "").trim();
    if (v && !BANK_ACCOUNT_TYPE_OPTIONS.includes(v)) {
      return [...BANK_ACCOUNT_TYPE_OPTIONS, v];
    }
    return BANK_ACCOUNT_TYPE_OPTIONS;
  }, [editableSettings.bankAccount]);

  const toggleSection = (section) => {
    setOpenStates(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchProjectData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/projects`, {
        params: {
          projectId: projectId,
          customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
        },
      });

      // API may return a plain array or a paginated envelope { items: [...], total, ... }
      const projectsList =
        response?.items ?? (Array.isArray(response) ? response : []);
      const projectData =
        projectsList.find(
          (p) =>
            String(p?.projectId ?? p?.id ?? "") === String(projectId ?? "")
        ) ?? projectsList[0];

      if (projectData) {
        setProject(projectData);

        const bankDetails = parseBankDetails(decryptFromTransit(projectData?.bankDetails) || "");
        setInitialBankDetails(bankDetails);

        setEditableSettings({
          ...bankDetails,
          stamp_duty_percent: projectData?.configuration?.stampDutyPercent || [],
          gst_percent: projectData?.configuration?.gstPercent || [],
          registration_percent: projectData?.configuration?.registrationPercent || [],
          costSheetTerms: projectData?.configuration?.costSheetTerms || "",
          bookingLetterTerms: projectData?.configuration?.bookingLetterTerms || "",
          cancellationLetterTerms: projectData?.configuration?.cancellationLetterTerms || "",
          handoverLetterTerms: projectData?.configuration?.handoverLetterTerms || ""
        });
        setDisplayLetters({
          CostSheet: projectData?.configuration?.costSheetTerms || "",
          bookingLetter: projectData?.configuration?.bookingLetterTerms || "",
          cancellationLetter: projectData?.configuration?.cancellationLetterTerms || "",
          handoverLetter: projectData?.configuration?.handoverLetterTerms || "",
        });
      }
    } catch (err) {
      setError(err.message || "Failed to fetch project data");
      console.error("Error fetching project:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [userInfo, projectId]);

  // v2: auto-select first phase if phases available
  useEffect(() => {
    if (projectPhases.length > 0 && !selectedPhaseId) {
      setSelectedPhaseId(projectPhases[0].projectPhaseId);
    }
  }, [projectPhases]);

  // v2: fetch phase-stage weights when a phase is selected
  useEffect(() => {
    if (!selectedPhaseId) return;
    const fetchWeights = async () => {
      try {
        const data = await getPhaseStageWeights({ projectPhaseId: selectedPhaseId });
        const weightsArray = Array.isArray(data) ? data : (data?.items || []);
        setPhaseStageWeights(weightsArray);
        setLocalPhaseStageWeights(weightsArray.map(w => ({ ...w })));
        setPhaseWeightsError(null);
      } catch (err) {
        console.error("Error fetching phase-stage weights:", err);
        setPhaseWeightsError("Failed to load stage weights.");
      }
    };
    fetchWeights();
  }, [selectedPhaseId]);

  const handleInputChange = (name, value) => {
    setEditableSettings(prev => ({ ...prev, [name]: value }));
  };
  const handleAddStampDutyPercent = (e) => {
    e.stopPropagation();
    if (editableSettings.stamp_duty_percent.length >= 3) {
      setGovtMaxExceeded((prev) => ({ ...prev, stamp: true }));
      return;
    }

    if (newStampDutyPercent) {
      setEditableSettings(prev => ({
        ...prev,
        stamp_duty_percent: [...prev.stamp_duty_percent, parseFloat(newStampDutyPercent)]
      }));
      setNewStampDutyPercent("");
    }
  };

  const handleAddGstPercent = (e) => {
    e.stopPropagation();
    if (editableSettings.gst_percent.length >= 3) {
      setGovtMaxExceeded((prev) => ({ ...prev, gst: true }));
      return;
    }

    if (newGstPercent) {
      setEditableSettings(prev => ({
        ...prev,
        gst_percent: [...prev.gst_percent, parseFloat(newGstPercent)]
      }));
      setNewGstPercent("");
    }
  };

  const handleAddRegistrationPercent = (e) => {
    e.stopPropagation();
    if (editableSettings.registration_percent.length >= 3) {
      setGovtMaxExceeded((prev) => ({ ...prev, registration: true }));
      return;
    }

    if (newRegistrationPercent) {
      setEditableSettings(prev => ({
        ...prev,
        registration_percent: [...prev.registration_percent, parseFloat(newRegistrationPercent)]
      }));
      setNewRegistrationPercent("");
    }
  };

  const handleRemoveStampDutyPercent = (e, index) => {
    e.stopPropagation();
    setEditableSettings((prev) => ({
      ...prev,
      stamp_duty_percent: prev.stamp_duty_percent.filter((_, i) => i !== index),
    }));
    setGovtMaxExceeded((prev) => ({ ...prev, stamp: false }));
  };

  const handleRemoveGstPercent = (e, index) => {
    e.stopPropagation();
    setEditableSettings((prev) => ({
      ...prev,
      gst_percent: prev.gst_percent.filter((_, i) => i !== index),
    }));
    setGovtMaxExceeded((prev) => ({ ...prev, gst: false }));
  };

  const handleRemoveRegistrationPercent = (e, index) => {
    e.stopPropagation();
    setEditableSettings((prev) => ({
      ...prev,
      registration_percent: prev.registration_percent.filter((_, i) => i !== index),
    }));
    setGovtMaxExceeded((prev) => ({ ...prev, registration: false }));
  };

  const formatBankDetailsString = (details) => {
    return `Bank Name: ${details.bankName},\nBank Account Type: ${details.bankAccount},\nAccount No: ${details.accountNo},\nBranch: ${details.branch},\nIFSC Code: ${details.ifscCode}`;
  };


  // `settingsOverride` carries the just-edited modal values so we save the fresh
  // values synchronously instead of reading a stale `editableSettings` closure
  // (setEditableSettings is async — a deferred handleSave() still saw the old state,
  // which is why govt charges / bank details were persisting as empty). `editingBank`
  // is passed explicitly for the same reason (the isEditingBankDetails flag was stale).
  const handleSave = async ({ settingsOverride = {}, editingBank = false } = {}) => {
    if (!project) {
      toast.error("Project data not loaded. Please try again.");
      return;
    }

    const settings = { ...editableSettings, ...settingsOverride };

    setUploadingImages(true);
    try {
      const formData = new FormData();
      formData.append("project_name", project.projectName);
      formData.append("developer", project.developer);
      formData.append("customer_id", userInfo?.loginIdentifierDetails?.customer?.customerId || project.customerId);
      formData.append("address_building_no", project.address?.buildingNo || "");
      formData.append("address_street_name1", project.address?.streetName1 || "");
      formData.append("address_street_name2", project.address?.streetName2 || "");
      formData.append("address_city", project.address?.city || "");
      formData.append("address_district", project.address?.district || "");
      formData.append("address_pin_code", project.address?.pinCode || "");
      formData.append("address_state", project.address?.state || "");
      formData.append("address_country", project.address?.country || "");
      formData.append("rera_no", project.reraNo || "");
      formData.append("amenities", project.amenities || "");
      formData.append("towers", project.towers || 0);
      formData.append("units", project.units || 0);
      formData.append("specifications", project.specifications || "");
      formData.append("location", project.location || "");
      formData.append("size_acres", project.sizeAcres || 0);
      formData.append("project_status", project.projectStatus || 0);

      // PUT /projects: only send bank_details when this save is from the Bank Details section,
      // and only as valid transit ciphertext (never plaintext — avoids backend Salted__ errors).
      if (editingBank) {
        const currentBankDetails = {
          bankName: settings.bankName,
          bankAccount: settings.bankAccount,
          accountNo: settings.accountNo,
          branch: settings.branch,
          ifscCode: settings.ifscCode,
        };
        const bankDetailsChanged =
          String(currentBankDetails.bankName || "").trim() !==
            String(initialBankDetails.bankName || "").trim() ||
          String(currentBankDetails.bankAccount || "").trim() !==
            String(initialBankDetails.bankAccount || "").trim() ||
          String(currentBankDetails.accountNo || "").trim() !==
            String(initialBankDetails.accountNo || "").trim() ||
          String(currentBankDetails.branch || "").trim() !==
            String(initialBankDetails.branch || "").trim() ||
          String(currentBankDetails.ifscCode || "").trim() !==
            String(initialBankDetails.ifscCode || "").trim();

        const hasAnyBankField = [
          currentBankDetails.bankName,
          currentBankDetails.bankAccount,
          currentBankDetails.accountNo,
          currentBankDetails.branch,
          currentBankDetails.ifscCode,
        ].some((v) => v != null && String(v).trim() !== "");

        if (bankDetailsChanged && hasAnyBankField) {
          const bankDetailsString = formatBankDetailsString(settings);
          const encrypted = encryptForTransit(bankDetailsString);
          if (!encrypted || encrypted === bankDetailsString) {
            toast.error(
              "Cannot save bank details: transit encryption key is not configured."
            );
            setUploadingImages(false);
            return;
          }
          formData.append(
            "bank_details",
            normalizeTransitCiphertextBase64(encrypted)
          );
          setInitialBankDetails(currentBankDetails);
        }
        // All bank fields empty: omit bank_details (leave server value unchanged).
      }

      // v2: wbsPercent JSON blob removed from configuration (§C: replaced by per-phase
      // stage-weights managed via getPhaseStageWeights/putPhaseStageWeights endpoints).
      formData.append("configuration", JSON.stringify({
        stamp_duty_percent: settings.stamp_duty_percent,
        gst_percent: settings.gst_percent,
        registration_percent: settings.registration_percent,
        cost_sheet_terms: settings.costSheetTerms,
        booking_letter_terms: settings.bookingLetterTerms,
        cancellation_letter_terms: settings.cancellationLetterTerms,
        handover_letter_terms: settings.handoverLetterTerms,
      }));

      // Backend expects loginId on PUT /projects (same as ChannelPartners / components/ProjectSettings).
      const response = await axiosInstance.put(`/projects`, formData, {
        params: {
          projectId: project.projectId,
          ...(userInfo?.loginId != null && { loginId: userInfo.loginId }),
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Saved successfully!");
      setIsEditingBankDetails(false);
      setIsEditingOtherSettings(false);
      setGovtMaxExceeded({ stamp: false, gst: false, registration: false });

    } catch (error) {
      console.error("API Error:", error);
      const detail =
        error?.response?.data?.detail ??
        error?.response?.data?.message ??
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null);
      toast.error(
        detail ? `Failed to update project: ${detail}` : "Failed to update project"
      );
    } finally {
      setUploadingImages(false);
    }
  };

  // v2: save phase-stage weights
  const handleSavePhaseWeights = async () => {
    if (!selectedPhaseId) return;
    // 100%-sum validation
    const activeWeights = localPhaseStageWeights.filter(w => w.isActive !== false);
    const weightSum = activeWeights.reduce((sum, w) => sum + (Number(w.weight) || 0), 0);
    if (activeWeights.length > 0 && Math.abs(weightSum - 100) > WBS_SUM_TOLERANCE) {
      toast.error(`Weights must add up to 100%. Current total: ${weightSum.toFixed(2)}%`);
      return;
    }
    setSavingPhaseWeights(true);
    try {
      const stages = localPhaseStageWeights.map(w => ({
        wbsStageId: w.wbsStageId,
        weight: Number(w.weight) || 0,
        isActive: w.isActive !== false,
      }));
      const result = await putPhaseStageWeights({ projectPhaseId: selectedPhaseId, stages });
      const resultArray = Array.isArray(result) ? result : (result?.items || []);
      setPhaseStageWeights(resultArray);
      setLocalPhaseStageWeights(resultArray.map(w => ({ ...w })));
      setIsEditingPhaseWeights(false);
      toast.success("Stage weights saved successfully!");
    } catch (err) {
      console.error("Error saving phase-stage weights:", err);
      toast.error("Failed to save stage weights.");
    } finally {
      setSavingPhaseWeights(false);
    }
  };

  const parseBankDetails = (bankDetailsString) => {
    const parts = bankDetailsString.split(",").map((part) => part.trim());
    const bankDetails = {
      bankName: "",
      bankAccount: "",
      accountNo: "",
      branch: "",
      ifscCode: "",
    };

    parts.forEach((part) => {
      if (part.startsWith("Bank Name:")) bankDetails.bankName = part.split(":")[1].trim();
      else if (part.startsWith("Bank Account Type:"))
        bankDetails.bankAccount = part.slice("Bank Account Type:".length).trim();
      else if (part.startsWith("Bank Account:"))
        bankDetails.bankAccount = part.slice("Bank Account:".length).trim();
      else if (part.startsWith("Account No:")) bankDetails.accountNo = part.split(":")[1].trim();
      else if (part.startsWith("Branch:")) bankDetails.branch = part.split(":")[1].trim();
      else if (part.startsWith("IFSC Code:")) bankDetails.ifscCode = part.split(":")[1].trim();
    });

    return bankDetails;
  };

  const handleEditOtherSettings = () => {
    setIsEditingOtherSettings(true);
    setGovtMaxExceeded({ stamp: false, gst: false, registration: false });
  };

  // v2: handlePercentageChange / wbsPercentKeys / wbsPercentTotal removed (§C: wbsPercent blob gone).

  return (
    <div className="w-full flex lg:flex-row flex-col gap-4">
      <div className="lg:w-2/3 w-full space-y-2">

        {/* v2: Phase-Stage Weights editor (replaces wbsPercent JSON blob — §C) */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 lg:p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-base lg:text-lg font-semibold text-slate-900">Phase Stage Weights</h1>
            {userInfo?.persona !== "STAFF" && (
              isEditingPhaseWeights ? (
                <div className="flex gap-2">
                  <OutlineButton
                    onClick={() => {
                      setIsEditingPhaseWeights(false);
                      setLocalPhaseStageWeights(phaseStageWeights.map(w => ({ ...w })));
                    }}
                    className={"w-32"}
                  >
                    Cancel
                  </OutlineButton>
                  <PrimaryButton
                    className={"w-30"}
                    onClick={handleSavePhaseWeights}
                    disabled={savingPhaseWeights}
                  >
                    {savingPhaseWeights ? "Saving..." : "Save"}
                  </PrimaryButton>
                </div>
              ) : (
                <EditButton onClick={() => setIsEditingPhaseWeights(true)} />
              )
            )}
          </div>

          {/* Phase selector */}
          {projectPhases.length > 0 && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-500 mb-1">Phase</label>
              <select
                value={selectedPhaseId || ""}
                onChange={(e) => setSelectedPhaseId(parseInt(e.target.value, 10))}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
              >
                {projectPhases.map(phase => (
                  <option key={phase.projectPhaseId} value={phase.projectPhaseId}>
                    {phase.name || `Phase ${phase.projectPhaseId}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {phaseWeightsError && (
            <p className="text-sm text-red-600 font-medium mb-3">{phaseWeightsError}</p>
          )}

          {/* v2: per-stage weight table driven by catalog (not free-form JSON keys) */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <table className="w-full border-collapse">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left font-semibold px-4 py-2.5">Stage</th>
                  <th className="text-left font-semibold px-4 py-2.5 w-44">Weight (%)</th>
                  <th className="text-left font-semibold px-4 py-2.5 w-24">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {localPhaseStageWeights.map((row, idx) => (
                  <tr key={row.wbsStageId || idx} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-2.5 text-sm text-slate-700">{row.label || row.typeName}</td>
                    <td className="px-4 py-2.5">
                      {isEditingPhaseWeights ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={row.weight}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setLocalPhaseStageWeights(prev =>
                              prev.map((w, i) => i === idx ? { ...w, weight: val } : w)
                            );
                          }}
                          className="w-24 px-2 py-1 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
                        />
                      ) : (
                        <span className="text-sm font-semibold text-slate-900 tabular-nums">
                          {row.weight}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-sm">
                      {isEditingPhaseWeights ? (
                        <input
                          type="checkbox"
                          checked={row.isActive !== false}
                          onChange={(e) => {
                            setLocalPhaseStageWeights(prev =>
                              prev.map((w, i) => i === idx ? { ...w, isActive: e.target.checked } : w)
                            );
                          }}
                          className="w-4 h-4"
                        />
                      ) : (
                        <span className={row.isActive !== false ? "text-emerald-600" : "text-slate-400"}>
                          {row.isActive !== false ? "Yes" : "No"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {localPhaseStageWeights.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-slate-400">
                      {selectedPhaseId ? "No stage weights found for this phase." : "Select a phase to view stage weights."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Weight sum indicator */}
          {localPhaseStageWeights.length > 0 && (() => {
            const activeWeights = localPhaseStageWeights.filter(w => w.isActive !== false);
            const sum = activeWeights.reduce((s, w) => s + (Number(w.weight) || 0), 0);
            const valid = Math.abs(sum - 100) <= WBS_SUM_TOLERANCE;
            return (
              <p className={`mt-2 text-xs font-medium ${valid ? "text-emerald-600" : "text-red-500"}`}>
                Active stage weights total: {sum.toFixed(2)}% {valid ? "✓" : "(must equal 100%)"}
              </p>
            );
          })()}
        </div>
        {/* End of Phase Stage Weights */}
      </div>

      <div className="lg:w-1/3 w-full flex flex-col gap-4 h-full">
        
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 lg:p-6">
          <div className="flex justify-between items-center mb-4 pb-10">
            <h1 className="text-base lg:text-lg font-semibold text-slate-900">Bank Details</h1>
            {userInfo?.persona !== "STAFF" && (
              <EditButton onClick={() => setBankDetailsModalOpen(true)} />
            )}
          </div>

          <div className="text-sm text-gray-600 space-y-4">
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <BankDetailRow label="Bank Name" value={editableSettings.bankName} />
                <BankDetailRow label="Bank account type" value={editableSettings.bankAccount} />
                <BankDetailRow label="Account No" value={editableSettings.accountNo} />
                <BankDetailRow label="Branch" value={editableSettings.branch} />
                <BankDetailRow label="IFSC Code" value={editableSettings.ifscCode} />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 lg:p-6">
          <div className="flex justify-between items-center pb-10">
            <h1 className="text-base lg:text-lg font-semibold text-slate-900">Govt Charges</h1>
            {userInfo?.persona !== "STAFF" && (
              <EditButton onClick={() => setGovtChargesModalOpen(true)} />
            )}
          </div>

          <div className="text-sm text-gray-600">
            <div className="flex-1">
              <PercentSection label="Stamp Duty %" values={editableSettings.stamp_duty_percent} />
              <PercentSection label="GST %" values={editableSettings.gst_percent} />
              <PercentSection label="Registration %" values={editableSettings.registration_percent} />
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details edit modal — uses its own Form context, mirrors the
          encrypted bank_details payload that handleSave already builds. */}
      <BankDetailsModal
        open={bankDetailsModalOpen}
        defaults={{
          bankName: editableSettings.bankName || "",
          bankAccount: editableSettings.bankAccount || "",
          accountNo: editableSettings.accountNo || "",
          branch: editableSettings.branch || "",
          ifscCode: editableSettings.ifscCode || "",
        }}
        bankAccountTypeOptions={BANK_ACCOUNT_TYPE_OPTIONS}
        saving={uploadingImages}
        onClose={() => setBankDetailsModalOpen(false)}
        onSave={async (values) => {
          // Push form values into editableSettings for display, and pass them
          // directly into handleSave so the PUT uses the fresh values (not the
          // stale closure snapshot). `editingBank` includes bank_details.
          setEditableSettings((prev) => ({ ...prev, ...values }));
          setIsEditingBankDetails(true);
          await handleSave({ settingsOverride: values, editingBank: true });
          setBankDetailsModalOpen(false);
        }}
      />

      {/* Govt Charges edit modal — three independent percent lists, each
          capped at 3 entries. Uses useFieldArray inside its own Form. */}
      <GovtChargesModal
        open={govtChargesModalOpen}
        defaults={{
          stamp_duty_percent: editableSettings.stamp_duty_percent || [],
          gst_percent: editableSettings.gst_percent || [],
          registration_percent: editableSettings.registration_percent || [],
        }}
        saving={uploadingImages}
        onClose={() => setGovtChargesModalOpen(false)}
        onSave={async (values) => {
          const govt = {
            stamp_duty_percent: values.stamp_duty_percent,
            gst_percent: values.gst_percent,
            registration_percent: values.registration_percent,
          };
          // Update display state and pass the fresh govt charges straight into
          // handleSave — avoids the stale-closure read that persisted empties.
          setEditableSettings((prev) => ({ ...prev, ...govt }));
          setIsEditingOtherSettings(true);
          await handleSave({ settingsOverride: govt });
          setGovtChargesModalOpen(false);
        }}
      />
    </div>
  );
};

// ─── Local presentation helpers ──────────────────────────────────────────

const BankDetailRow = ({ label, value }) => (
  <div>
    <label className="font-medium text-gray-700">{label}:</label>
    <p className="text-slate-800">{value || <span className="text-slate-400">—</span>}</p>
  </div>
);

const PercentSection = ({ label, values }) => (
  <div className="py-2">
    <label className="font-medium text-gray-700 py-2 block">{label}:</label>
    {(!values || values.length === 0) ? (
      <p className="text-slate-400 text-sm">No options configured</p>
    ) : (
      values.map((percent, index) => (
        <p key={index}>Option {index + 1}: {percent}%</p>
      ))
    )}
  </div>
);

// ─── Bank Details Modal ──────────────────────────────────────────────────

const BankDetailsModal = ({ open, defaults, bankAccountTypeOptions, saving, onClose, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: defaults,
    resolver: yupResolver(projectBankDetailsSchema),
    mode: "onTouched",
  });

  useEffect(() => {
    if (open) reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <FormModal open={open} onClose={onClose} title="Edit Bank Details" size="md">
      <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CustomInput label="Bank Name" error={errors.bankName?.message} {...register("bankName")} />
          <div>
            <label className="flex text-sm font-normal text-slate-500 mb-1">Bank account type</label>
            <select
              {...register("bankAccount")}
              className="w-full px-2 py-2 border border-slate-300 rounded-md focus:border-flamingo-500 focus:ring-0 focus:outline-none text-sm bg-white"
            >
              <option value="">Select account type</option>
              {bankAccountTypeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <CustomInput label="Account No" error={errors.accountNo?.message} {...register("accountNo")} />
          <CustomInput label="Branch" error={errors.branch?.message} {...register("branch")} />
          <CustomInput label="IFSC Code" className="md:col-span-2" error={errors.ifscCode?.message} {...register("ifscCode")} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <OutlineButton type="button" onClick={onClose} className="!w-auto">Cancel</OutlineButton>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </FormModal>
  );
};

// ─── Govt Charges Modal ──────────────────────────────────────────────────

const PercentArrayEditor = ({ control, register, name, label, max = 3 }) => {
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <div className="space-y-2">
      <label className="font-medium text-gray-700 block">{label}</label>
      {fields.length === 0 && (
        <p className="text-xs text-slate-400">No options configured.</p>
      )}
      {fields.map((field, index) => (
        <div key={field.id} className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            {...register(`${name}.${index}`, { valueAsNumber: true })}
            className="flex-1 px-2 py-1.5 border border-slate-300 rounded-md focus:border-flamingo-500 focus:ring-0 focus:outline-none text-sm"
          />
          <span className="text-sm text-slate-500">%</span>
          <button
            type="button"
            onClick={() => remove(index)}
            className="text-sm text-rose-600 hover:text-rose-800 font-medium shrink-0"
          >
            Delete
          </button>
        </div>
      ))}
      {fields.length < max && (
        <button
          type="button"
          onClick={() => append(0)}
          className="text-sm text-flamingo-600 hover:text-flamingo-700 font-medium"
        >
          + Add option
        </button>
      )}
      {fields.length >= max && (
        <p className="text-xs text-slate-500">Maximum {max} options reached.</p>
      )}
    </div>
  );
};

const GovtChargesModal = ({ open, defaults, saving, onClose, onSave }) => {
  const { register, control, handleSubmit, reset } = useForm({
    defaultValues: defaults,
    resolver: yupResolver(projectGovtChargesSchema),
    mode: "onTouched",
  });

  useEffect(() => {
    if (open) reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <FormModal open={open} onClose={onClose} title="Edit Govt Charges" size="lg">
      <form onSubmit={handleSubmit(onSave)} className="space-y-6" noValidate>
        <p className="text-sm text-slate-600">
          Add, remove, or change percent options below. Each list is capped at 3 entries.
        </p>
        <PercentArrayEditor control={control} register={register} name="stamp_duty_percent" label="Stamp Duty %" />
        <PercentArrayEditor control={control} register={register} name="gst_percent" label="GST %" />
        <PercentArrayEditor control={control} register={register} name="registration_percent" label="Registration %" />
        <div className="flex justify-end gap-2 pt-2">
          <OutlineButton type="button" onClick={onClose} className="!w-auto">Cancel</OutlineButton>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </FormModal>
  );
};

export default ProjectSettings;